import { Check, Copy } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@nucleus-commerce/ui/components/sheet';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

export interface PreviewPayloadSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The button that opens the sheet. */
  trigger: ReactNode;
  title: string;
  description: ReactNode;
  /** Accessible name of the JSON block. */
  label: string;
  /** Pretty-printed as is; undefined shows `empty` (or the skeleton while pending). */
  payload: unknown;
  isPending: boolean;
  error?: unknown;
  /** Shown when there is no payload, no error and nothing pending. */
  empty?: string;
  /** Extra controls on the toolbar row, left of the copy button. */
  controls?: ReactNode;
}

/**
 * A side sheet with a storefront payload as JSON and a copy button. The section preview and the
 * block editor's preview panel both use it; each owns its query and passes the result in.
 */
export function PreviewPayloadSheet({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  label,
  payload,
  isPending,
  error,
  empty,
  controls,
}: PreviewPayloadSheetProps) {
  const { t } = useTranslation('content');
  const message = useErrorMessage();
  const [copied, setCopied] = useState(false);
  const json = payload === undefined ? '' : JSON.stringify(payload, null, 2);

  const copy = async () => {
    await navigator.clipboard.writeText(json);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent className="flex w-full flex-col gap-4 sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>
        <div className="flex items-center justify-between gap-2 px-4">
          <div className="flex items-center gap-2">{controls}</div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!json}
            onClick={() => void copy()}
          >
            {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
            {copied ? t('preview.copied') : t('preview.copy')}
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto px-4 pb-4">
          {error ? (
            <p role="alert" className="text-destructive text-sm">
              {message(error)}
            </p>
          ) : json ? (
            <pre
              className="bg-muted rounded-md p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap"
              aria-label={label}
            >
              {json}
            </pre>
          ) : isPending ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-4" />
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">{empty}</p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
